/* eslint-disable */
/* prettier-ignore */
// Generated by elegant-router
// Read more: https://github.com/soybeanjs/elegant-router

import type { RouteRecordRaw, RouteComponent } from 'vue-router';
import type { ElegantConstRoute } from '@elegant-router/vue';
import type { RouteMap, RouteKey, RoutePath } from '@elegant-router/types';

/**
 * transform elegant const routes to vue routes
 * @param routes elegant const routes
 * @param layouts layout components
 * @param views view components
 */
export function transformElegantRoutesToVueRoutes(
  routes: ElegantConstRoute[],
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  return routes.flatMap(route => transformElegantRouteToVueRoute(route, layouts, views));
}

/**
 * transform elegant route to vue route
 * @param route elegant const route
 * @param layouts layout components
 * @param views view components
 */
function transformElegantRouteToVueRoute(
  route: ElegantConstRoute,
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  const LAYOUT_PREFIX = 'layout.';
  const VIEW_PREFIX = 'view.';
  const ROUTE_DEGREE_SPLITTER = '_';
  const FIRST_LEVEL_ROUTE_COMPONENT_SPLIT = '$';

  function isLayout(component: string) {
    return component.startsWith(LAYOUT_PREFIX);
  }

  function getLayoutName(component: string) {
    const layout = component.replace(LAYOUT_PREFIX, '');

    if(!layouts[layout]) {
      throw new Error(`Layout component "${layout}" not found`);
    }

    return layout;
  }

  function isView(component: string) {
    return component.startsWith(VIEW_PREFIX);
  }

  function getViewName(component: string) {
    const view = component.replace(VIEW_PREFIX, '');

    if(!views[view]) {
      throw new Error(`View component "${view}" not found`);
    }

    return view;
  }

  function isFirstLevelRoute(item: ElegantConstRoute) {
    return !item.name.includes(ROUTE_DEGREE_SPLITTER);
  }

  function isSingleLevelRoute(item: ElegantConstRoute) {
    return isFirstLevelRoute(item) && !item.children?.length;
  }

  function getSingleLevelRouteComponent(component: string) {
    const [layout, view] = component.split(FIRST_LEVEL_ROUTE_COMPONENT_SPLIT);

    return {
      layout: getLayoutName(layout),
      view: getViewName(view)
    };
  }

  const vueRoutes: RouteRecordRaw[] = [];

  // add props: true to route
  if (route.path.includes(':') && !route.props) {
    route.props = true;
  }

  const { name, path, component, children, ...rest } = route;

  const vueRoute = { name, path, ...rest } as RouteRecordRaw;

  try {
    if (component) {
      if (isSingleLevelRoute(route)) {
        const { layout, view } = getSingleLevelRouteComponent(component);

        const singleLevelRoute: RouteRecordRaw = {
          path,
          component: layouts[layout],
          meta: {
            title: route.meta?.title || ''
          },
          children: [
            {
              name,
              path: '',
              component: views[view],
              ...rest
            } as RouteRecordRaw
          ]
        };

        return [singleLevelRoute];
      }

      if (isLayout(component)) {
        const layoutName = getLayoutName(component);

        vueRoute.component = layouts[layoutName];
      }

      if (isView(component)) {
        const viewName = getViewName(component);

        vueRoute.component = views[viewName];
      }

    }
  } catch (error: any) {
    console.error(`Error transforming route "${route.name}": ${error.toString()}`);
    return [];
  }

  // add redirect to child
  if (children?.length && !vueRoute.redirect) {
    vueRoute.redirect = {
      name: children[0].name
    };
  }

  if (children?.length) {
    const childRoutes = children.flatMap(child => transformElegantRouteToVueRoute(child, layouts, views));

    if(isFirstLevelRoute(route)) {
      vueRoute.children = childRoutes;
    } else {
      vueRoutes.push(...childRoutes);
    }
  }

  vueRoutes.unshift(vueRoute);

  return vueRoutes;
}

/**
 * map of route name and route path
 */
const routeMap: RouteMap = {
  "root": "/",
  "not-found": "/:pathMatch(.*)*",
  "exception": "/exception",
  "exception_403": "/exception/403",
  "exception_404": "/exception/404",
  "exception_500": "/exception/500",
  "document": "/document",
  "document_project": "/document/project",
  "document_project-link": "/document/project-link",
  "document_vue": "/document/vue",
  "document_vite": "/document/vite",
  "document_unocss": "/document/unocss",
  "document_naive": "/document/naive",
  "document_antd": "/document/antd",
  "403": "/403",
  "404": "/404",
  "500": "/500",
  "about": "/about",
  "analytics": "/analytics",
  "analytics_business-daily-report": "/analytics/business-daily-report",
  "analytics_daily-report-analysis": "/analytics/daily-report-analysis",
  "analytics_direct-supply-record": "/analytics/direct-supply-record",
  "analytics_measurement-tracking": "/analytics/measurement-tracking",
  "analytics_weighing-record-report": "/analytics/weighing-record-report",
  "approval": "/approval",
  "base-goods": "/base-goods",
  "base-goods_appointment": "/base-goods/appointment",
  "base-goods_inbound": "/base-goods/inbound",
  "base-goods_inbound-images": "/base-goods/inbound-images",
  "base-goods_inbound-quality": "/base-goods/inbound-quality",
  "base-goods_inbound-weight": "/base-goods/inbound-weight",
  "base-goods_outbound": "/base-goods/outbound",
  "base-goods_outbound-images": "/base-goods/outbound-images",
  "base-goods_outbound-quality": "/base-goods/outbound-quality",
  "base-goods_outbound-weight": "/base-goods/outbound-weight",
  "base-goods_purchase-waybill": "/base-goods/purchase-waybill",
  "base-goods_sales-waybill": "/base-goods/sales-waybill",
  "basedata": "/basedata",
  "basedata_customer": "/basedata/customer",
  "basedata_driver": "/basedata/driver",
  "basedata_formula-lib": "/basedata/formula-lib",
  "basedata_product-name": "/basedata/product-name",
  "basedata_pub-master-corp": "/basedata/pub-master-corp",
  "basedata_pub-master-person": "/basedata/pub-master-person",
  "basedata_supplier": "/basedata/supplier",
  "batch": "/batch",
  "batch_batch-process": "/batch/batch-process",
  "contact-center": "/contact-center",
  "contract": "/contract",
  "contract_purchase": "/contract/purchase",
  "contract_purchase_detail": "/contract/purchase/detail",
  "contract_sales": "/contract/sales",
  "contract_transport": "/contract/transport",
  "demo-route": "/demo-route",
  "demo-route_child": "/demo-route/child",
  "energy": "/energy",
  "energy_management": "/energy/management",
  "finance": "/finance",
  "finance_purchase-payment": "/finance/purchase-payment",
  "finance_sales-receipt": "/finance/sales-receipt",
  "function": "/function",
  "function_hide-child": "/function/hide-child",
  "function_hide-child_one": "/function/hide-child/one",
  "function_hide-child_three": "/function/hide-child/three",
  "function_hide-child_two": "/function/hide-child/two",
  "function_multi-tab": "/function/multi-tab",
  "function_request": "/function/request",
  "function_super-page": "/function/super-page",
  "function_tab": "/function/tab",
  "function_toggle-auth": "/function/toggle-auth",
  "headquarters": "/headquarters",
  "headquarters_contracts": "/headquarters/contracts",
  "headquarters_statistics": "/headquarters/statistics",
  "home": "/home",
  "iframe-page": "/iframe-page/:url",
  "inventory": "/inventory",
  "inventory_check": "/inventory/check",
  "inventory_finished": "/inventory/finished",
  "inventory_material": "/inventory/material",
  "inventory_production": "/inventory/production",
  "invoice": "/invoice",
  "invoice_purchase-invoice": "/invoice/purchase-invoice",
  "invoice_purchase-invoice_in": "/invoice/purchase-invoice/in",
  "invoice_purchase-invoice_out": "/invoice/purchase-invoice/out",
  "invoice_sales-invoice": "/invoice/sales-invoice",
  "jin-sheng-lan": "/jin-sheng-lan",
  "jin-sheng-lan_purchase-detail-ledger": "/jin-sheng-lan/purchase-detail-ledger",
  "jin-sheng-lan_sales-detail-ledger": "/jin-sheng-lan/sales-detail-ledger",
  "ledger": "/ledger",
  "ledger_purchase-ledger": "/ledger/purchase-ledger",
  "ledger_purchase-ledger-v2": "/ledger/purchase-ledger-v2",
  "ledger_purchase-ledger-v3": "/ledger/purchase-ledger-v3",
  "ledger_purchase-ledger-v3-detail": "/ledger/purchase-ledger-v3-detail",
  "ledger_purchaser-settlement-center-leger-data": "/ledger/purchaser-settlement-center-leger-data",
  "ledger_purchaser-settlement-center-leger-data_detail": "/ledger/purchaser-settlement-center-leger-data/detail",
  "ledger_sales-ledger": "/ledger/sales-ledger",
  "ledger_sales-ledger-v2": "/ledger/sales-ledger-v2",
  "ledger_sales-ledger-v3": "/ledger/sales-ledger-v3",
  "ledger_sales-ledger-v3-detail": "/ledger/sales-ledger-v3-detail",
  "login": "/login/:module(pwd-login|code-login|register|reset-pwd|bind-wechat)?",
  "manage": "/manage",
  "manage_approval-admin": "/manage/approval-admin",
  "manage_dict": "/manage/dict",
  "manage_menu": "/manage/menu",
  "manage_notice": "/manage/notice",
  "manage_org": "/manage/org",
  "manage_position": "/manage/position",
  "manage_role": "/manage/role",
  "manage_user": "/manage/user",
  "manage_user-detail": "/manage/user-detail/:id",
  "millresident": "/millresident",
  "millresident_unload-manifest": "/millresident/unload-manifest",
  "monitor": "/monitor",
  "monitor_cache": "/monitor/cache",
  "monitor_file": "/monitor/file",
  "monitor_logs": "/monitor/logs",
  "monitor_logs_error": "/monitor/logs/error",
  "monitor_logs_login": "/monitor/logs/login",
  "monitor_logs_operation": "/monitor/logs/operation",
  "monitor_logs_scheduler": "/monitor/logs/scheduler",
  "monitor_scheduler": "/monitor/scheduler",
  "monitor_system": "/monitor/system",
  "multi-menu": "/multi-menu",
  "multi-menu_first": "/multi-menu/first",
  "multi-menu_first_child": "/multi-menu/first/child",
  "multi-menu_second": "/multi-menu/second",
  "multi-menu_second_child": "/multi-menu/second/child",
  "multi-menu_second_child_home": "/multi-menu/second/child/home",
  "nonbase-goods": "/nonbase-goods",
  "nonbase-goods_factory-images": "/nonbase-goods/factory-images",
  "nonbase-goods_factory-quality": "/nonbase-goods/factory-quality",
  "nonbase-goods_factory-weight": "/nonbase-goods/factory-weight",
  "nonbase-goods_measurement-tracking": "/nonbase-goods/measurement-tracking",
  "nonbase-goods_nonbase": "/nonbase-goods/nonbase",
  "nonbase-goods_nonbase-waybill": "/nonbase-goods/nonbase-waybill",
  "nonbase-goods_sales-detail-account": "/nonbase-goods/sales-detail-account",
  "nonbase-goods_steel-settlement": "/nonbase-goods/steel-settlement",
  "nonbase-goods_tracks-delivery": "/nonbase-goods/tracks-delivery",
  "nonbase-goods_tracks-delivery_detail": "/nonbase-goods/tracks-delivery/detail",
  "price": "/price",
  "price_disposal-material": "/price/disposal-material",
  "price_material": "/price/material",
  "price_material-maintenance": "/price/material-maintenance",
  "report": "/report",
  "report_daily-business-report-2": "/report/daily-business-report-2",
  "report_daily-business-report-2_scrap-business-report": "/report/daily-business-report-2/scrap-business-report",
  "report_factory-daily-report": "/report/factory-daily-report",
  "report_measurement-daily-report": "/report/measurement-daily-report",
  "report_sales-performance": "/report/sales-performance",
  "report_scrap-business-report": "/report/scrap-business-report",
  "report_weekly-business-report": "/report/weekly-business-report",
  "settlement": "/settlement",
  "settlement_purchase-settlement": "/settlement/purchase-settlement",
  "settlement_purchase-settlement_review": "/settlement/purchase-settlement/review",
  "settlement_purchase-settlement-center": "/settlement/purchase-settlement-center",
  "settlement_sales-settlement": "/settlement/sales-settlement",
  "settlement_sales-settlement-center": "/settlement/sales-settlement-center",
  "tools": "/tools",
  "tools_generate-table": "/tools/generate-table",
  "unified-management": "/unified-management",
  "unified-management_facility": "/unified-management/facility",
  "unified-management_files": "/unified-management/files",
  "unified-management_purchase": "/unified-management/purchase",
  "unified-management_sales": "/unified-management/sales",
  "user-center": "/user-center"
};

/**
 * get route path by route name
 * @param name route name
 */
export function getRoutePath<T extends RouteKey>(name: T) {
  return routeMap[name];
}

/**
 * get route name by route path
 * @param path route path
 */
export function getRouteName(path: RoutePath) {
  const routeEntries = Object.entries(routeMap) as [RouteKey, RoutePath][];

  const routeName: RouteKey | null = routeEntries.find(([, routePath]) => routePath === path)?.[0] || null;

  return routeName;
}
